import {
    deleteUniversalOrderSetting,
    DeleteUniversalOrderSettingParams,
    getUniversalOrderSettingList,
    GetUniversalOrderSettingListParams,
    saveUniversalOrderSetting,
    SaveUniversalOrderSettingParams,
    UniversalOrderSetting,
    UniversalOrderSettingType,
} from "@/services/api/universalOrderSetting";
import GlobalStore from "@/store";
import { computed, observable, makeObservable, actionBound } from "mobx";
import OrderTab from "../scenes/OrderManagement/enum/OrderTab";
import { unionBy } from "lodash";

class UniversalOrderSettingStore {
    companyLoading = false;

    shopLoadingMap = new Map<number, boolean>();

    settings: UniversalOrderSetting[] = [];

    buildScopeKey({
        shopId,
        companyId,
        type,
        orderTab,
    }: {
        shopId?: number;
        companyId?: number;
        type: UniversalOrderSettingType;
        orderTab?: OrderTab;
    }) {
        return `${shopId ?? companyId}-${type}-${orderTab || ""}`;
    }

    get scopeSettingsMap() {
        const map = new Map<string, UniversalOrderSetting>();

        this.settings.forEach((setting) => {
            map.set(this.buildScopeKey(setting), setting);
        });

        return map;
    }

    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            companyLoading: observable,
            shopLoadingMap: observable,
            settings: observable,
            buildScopeKey: actionBound,
            scopeSettingsMap: computed,
            setLoading: actionBound,
            fetchSettings: actionBound,
            saveSetting: actionBound,
            deleteSetting: actionBound,
        });
    }

    setLoading(shopId: number | undefined, loading: boolean) {
        if (shopId) {
            this.shopLoadingMap.set(shopId, loading);
        } else {
            this.companyLoading = loading;
        }
    }

    async fetchSettings(params: GetUniversalOrderSettingListParams, force = false) {
        const scopeKey = this.buildScopeKey(params);

        // 如果已有缓存并且不是强制 fetch，就直接返回
        if (!force && this.scopeSettingsMap.has(scopeKey)) {
            return this.scopeSettingsMap.get(scopeKey);
        }

        try {
            this.setLoading(params.shopId, true);
            const settings = await getUniversalOrderSettingList(params);

            this.settings = unionBy([...this.settings, ...settings], ({ id }) => id);
        } finally {
            this.setLoading(params.shopId, false);
        }
    }

    async saveSetting(params: SaveUniversalOrderSettingParams, hideModal = false) {
        try {
            this.setLoading(params.shopId, true);
            const setting = await saveUniversalOrderSetting(params, hideModal);

            const index = this.settings.findIndex((it) => it.id === setting.id);

            if (index > -1) {
                this.settings[index] = setting;
            }
        } finally {
            this.setLoading(params.shopId, false);
        }
    }

    async deleteSetting(shopId: number, params: DeleteUniversalOrderSettingParams) {
        try {
            this.setLoading(shopId, true);
            await deleteUniversalOrderSetting(params);

            const index = this.settings.findIndex((it) => it.id === params.id);

            if (index > -1) {
                this.settings.splice(index, 1);
            }
        } finally {
            this.setLoading(shopId, false);
        }
    }
}

export default UniversalOrderSettingStore;
